import { Action, ActionReducer } from '@ngrx/store';
import { Record } from 'immutable';
import { Entity } from 'src/util/system-model/generated/traits/entity';
import * as EntityStoreActions from './entity-store.actions';
import { EntityMeta } from './entity-store.models';

type EntityAction = EntityStoreActions.All;

function metaPath({ type, key }: { type: string; key: string }) {
  return [toCamelCase(type), 'meta', key];
}
function entityPath({ type, key }: { type: string; key: string }) {
  return [toCamelCase(type), 'entities', key];
}

function toCamelCase(key: string) {
  key =
    key
      ?.replace(/[^a-zA-Z0-9]+/g, ' ')
      .split(' ')
      .map((k) => k.charAt(0).toLocaleUpperCase() + k.slice(1))
      .join('') ?? '';
  return key.charAt(0).toLocaleLowerCase() + key.slice(1);
}

export type EntityReviver = ({
  state,
  type,
}: {
  state: Entity;
  type: string;
}) => Entity | undefined;

export function createEntityStoresReducer<T extends Record<any>>(
  initialState: T,
): ActionReducer<T, Action> {
  function reducer(state: T = initialState, action: EntityAction): T {
    switch (action.type) {
      case EntityStoreActions.ENTITIES_RECEIVED: {
        const result = state.withMutations((mutable) => {
          action.entities
            .filter((e) => e.source !== 'Removed')
            .forEach((changedOrAdded) => {
              const newState =
                changedOrAdded.source !== 'Removed' ? changedOrAdded.state : undefined;
              mutable.updateIn(entityPath(changedOrAdded), (oldState: Entity | undefined) => {
                if (
                  oldState != null &&
                  typeof oldState === 'object' &&
                  'dateLastUpdated' in oldState &&
                  oldState.dateLastUpdated instanceof Date
                ) {
                  const newStateNewer =
                    newState == null
                      ? false
                      : oldState.dateLastUpdated.getTime() < newState.dateLastUpdated.getTime();
                  if (!newStateNewer) {
                    return oldState;
                  }
                }
                return newState;
              });
              mutable.updateIn(metaPath(changedOrAdded), (meta) => {
                if (meta instanceof EntityMeta) {
                  return meta.markAsLoaded();
                }
                return new EntityMeta().markAsLoaded();
              });
            });

          action.entities
            .filter((e) => e.source === 'Removed')
            .forEach((removed) => {
              mutable.removeIn(metaPath(removed));
              mutable.removeIn(entityPath(removed));
            });
        });
        return result;
      }
      case EntityStoreActions.FETCH_ENTITIES: {
        return state.withMutations((mutable) => {
          action.fetched.forEach((fetched) => {
            const { type, id } = fetched;
            mutable.updateIn([type, 'meta', id], (meta) => {
              if (meta instanceof EntityMeta) {
                return meta.markAsFetching();
              }
              return new EntityMeta().markAsFetching();
            });
          });
        });
      }
      default:
        return state;
    }
  }
  return reducer as ActionReducer<T, Action>;
}
